import { useOrgAuthStore } from "@solomon-apps/shared";
import {
    Alert,
    AlertDescription,
    AlertTitle
} from "@solomon-apps/shared/src/components/ui/alert";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { useReducedMotion } from "motion/react";
import {
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import { useClientPlatformAccess } from "@/hooks/useClientPlatformAccess";
import { submitForReview } from "@/services/clientOnboarding";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { ALL_SCENARIOS } from "./mockScenarios";
import { OnboardingApplicationProvider } from "./OnboardingApplicationContext";
import { OnboardingPanel } from "./OnboardingPanel";
import { CelebrationCard } from "./OnboardingShell";
import { OnboardingShell } from "./OnboardingShell";
import {
    OnboardingSubmitProvider,
    type OnboardingSubmitValue
} from "./onboardingSubmitContext";
import { scenarioFromServer } from "./scenarioFromServer";
import {
    applicationPhase as deriveApplicationPhase,
    isSubmittable,
    type OnboardingScenario,
    orderByCategory,
    type SubsectionId
} from "./types";
import { useApprovalAcknowledgement } from "./useApprovalAcknowledgement";
import { useOnboardingApplication } from "./useOnboardingApplication";

/**
 * Onboarding hub — single page in the yaas-client app at `/onboarding`.
 * Renders the two-column 65/35 layout (compact list on the left; sticky
 * detail panel on the right at `lg+`). Below `lg` the detail panel hides
 * and the list goes full-width.
 *
 * Live data path: `useOnboardingApplication` fetches the (single)
 * application bundle for the calling client; `scenarioFromServer`
 * overlays the live step statuses onto the baseline subsection copy.
 *
 * Dev/test fallback: `?scenario=<idx>` query param renders one of the
 * mock scenarios from `mockScenarios.ts` instead. Useful for stepping
 * through visual states without a real backend.
 */
const SLA = "2–3 business days";

function readMockScenarioIdx(): number | null {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("scenario");
    if (!value) return null;
    const idx = parseInt(value, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= ALL_SCENARIOS.length)
        return null;
    return idx;
}

function isFrozen(state: OnboardingScenario["state"]): boolean {
    return (
        state === "UnderReview" ||
        state === "AwaitingAgreements" ||
        state === "InformationRequested" ||
        state === "Declined"
    );
}

/**
 * Approval-celebration animation cadence. On entry every section is
 * already showing its "Approved" badge (page loaded fully approved).
 * After a brief hold the left-side checkmark stagger and the
 * right-side celebration card start together — the right side runs
 * roughly 2x slower than the stagger, so the two sequences feel
 * concurrent rather than back-to-back. The card auto-routes to the
 * dashboard once it's settled (see autoRedirectMs below).
 */
const APPROVAL_INITIAL_HOLD_MS = 500;
const APPROVAL_STEP_MS = 180;
/** Total time the user is on the celebration card before the
 *  auto-redirect fires. Generous enough to read the slow-paced right
 *  side and still feel intentional. */
const APPROVAL_AUTO_REDIRECT_MS = 5000;

export function OnboardingHub(): ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const { consoleAvailable } = useClientPlatformAccess();
    const { bundle, initialLoading, error, refetch, applyStepUpdate } =
        useOnboardingApplication();

    const mockIdx = readMockScenarioIdx();
    const mockScenario = mockIdx !== null ? ALL_SCENARIOS[mockIdx] : null;

    const orgName =
        me?.org.legalName?.trim() ||
        me?.org.name?.trim() ||
        "your organization";

    const scenario = useMemo<OnboardingScenario | null>(() => {
        if (mockScenario) return mockScenario;
        if (!bundle) return null;
        return scenarioFromServer({
            application: bundle.application,
            steps: bundle.steps,
            orgName,
            sla: SLA
        });
    }, [mockScenario, bundle, orgName]);

    // The hook owns both the acknowledgement and the "still celebrating"
    // latch, because acknowledging is what flips `scenario.celebrate` false
    // underneath us. Deriving the flag here instead would unmount the card
    // mid-flight, before its Continue could route into the console.
    const {
        celebrating: isLiveApprovalCelebration,
        phase: approvalAckPhase,
        requestContinue,
        retry: retryApprovalAck
    } = useApprovalAcknowledgement({
        applicationUuid:
            !mockScenario && scenario?.state === "Approved" && bundle
                ? bundle.application.uuid
                : null,
        firstVisit: Boolean(scenario?.celebrate),
        allowConsoleNavigation: consoleAvailable
    });

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleSubmit = useCallback(async (): Promise<void> => {
        if (!bundle) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await submitForReview(bundle.application.uuid);
            await refetch();
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Onboarding was not submitted. Complete every required section and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    }, [bundle, refetch]);

    const frozen = scenario ? isFrozen(scenario.state) : false;
    const isApprovalCelebration =
        scenario?.state === "Approved" &&
        (Boolean(mockScenario?.celebrate) || isLiveApprovalCelebration);
    const reduced = useReducedMotion();

    // Visible (applicant-facing) sections in **render order**. The hub
    // groups subsections by category (CATEGORY_ORDER); declaration order
    // is only the tiebreaker within a category. Walking the scenario's
    // flat array directly puts agreements-category items (the two
    // signings) ahead of the sections that precede them on screen, even
    // though the agreements category renders last.
    const visibleSections = useMemo(() => {
        if (!scenario) return [];
        return orderByCategory(
            scenario.subsections.filter(
                (s) => s.treatment !== "solomon-run" && s.isApplicable
            ),
            (s) => s.category
        );
    }, [scenario]);

    const [revealedCount, setRevealedCount] = useState(0);
    const [showCelebration, setShowCelebration] = useState(false);

    useEffect(() => {
        if (!isApprovalCelebration) {
            setRevealedCount(0);
            setShowCelebration(false);
            return;
        }
        if (reduced) {
            setRevealedCount(visibleSections.length);
            setShowCelebration(true);
            return;
        }
        setRevealedCount(0);
        setShowCelebration(false);
        const cleanup: { interval: number | null } = { interval: null };
        const initialHandle = window.setTimeout(() => {
            setShowCelebration(true);
            setRevealedCount(1);
            if (visibleSections.length <= 1) return;
            let revealed = 1;
            const intervalHandle = window.setInterval(() => {
                revealed += 1;
                setRevealedCount(revealed);
                if (revealed >= visibleSections.length) {
                    window.clearInterval(intervalHandle);
                }
            }, APPROVAL_STEP_MS);
            cleanup.interval = intervalHandle;
        }, APPROVAL_INITIAL_HOLD_MS);
        return (): void => {
            window.clearTimeout(initialHandle);
            if (cleanup.interval !== null) {
                window.clearInterval(cleanup.interval);
            }
        };
    }, [isApprovalCelebration, reduced, visibleSections.length]);

    const celebratingIds = useMemo<Set<SubsectionId> | undefined>(() => {
        if (!isApprovalCelebration) return undefined;
        const set = new Set<SubsectionId>();
        for (let i = 0; i < revealedCount && i < visibleSections.length; i++) {
            set.add(visibleSections[i].id);
        }
        return set;
    }, [isApprovalCelebration, revealedCount, visibleSections]);

    // The Review and submit section owns the Submit button, so the action
    // travels to it through a context rather than the page footer. Built
    // before the early returns below so the hook order never changes.
    const submitValue = useMemo<OnboardingSubmitValue>(
        () => ({
            submit:
                !submitting && bundle
                    ? (): void => void handleSubmit()
                    : undefined,
            submitting,
            submittable: scenario ? isSubmittable(scenario.subsections) : false,
            error: submitError
        }),
        [submitting, bundle, handleSubmit, scenario, submitError]
    );

    if (initialLoading && !mockScenario) {
        return (
            <main className="min-h-dvh bg-surface-ground">
                <div className="max-w-3xl mx-auto pt-10 pb-50 lg:max-w-7xl" />
            </main>
        );
    }
    if (error && !mockScenario) {
        return (
            <main className="min-h-dvh bg-surface-ground">
                <div className="max-w-3xl mx-auto pt-10 pb-50 lg:max-w-7xl">
                    <Alert variant="destructive" className="px-5 py-4">
                        <AlertTitle>
                            Couldn&apos;t load your onboarding
                        </AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                </div>
            </main>
        );
    }
    if (!scenario) {
        return (
            <main className="min-h-dvh bg-surface-ground">
                <div className="max-w-3xl mx-auto pt-10 pb-50 lg:max-w-7xl">
                    <Alert variant="info" className="px-5 py-4">
                        <AlertTitle>No onboarding application yet</AlertTitle>
                        <AlertDescription>
                            Reach out to your BD contact to get your onboarding
                            invitation. Once it&apos;s issued you can complete
                            it from here.
                        </AlertDescription>
                    </Alert>
                </div>
            </main>
        );
    }

    const panel = (
        <OnboardingPanel
            subsections={scenario.subsections}
            applicationFrozen={frozen || isApprovalCelebration}
            applicationPhase={deriveApplicationPhase(scenario.state)}
            triggers={scenario.triggers}
            // The panel owns which subsection is open, so the hub has nothing
            // to do on the event; the prop is required, not optional.
            onOpen={(): void => undefined}
            celebratingIds={celebratingIds}
            rightOverride={
                isApprovalCelebration ? (
                    showCelebration ? (
                        <>
                            <CelebrationCard
                                layout="panel"
                                autoRedirectMs={
                                    consoleAvailable &&
                                    (!isLiveApprovalCelebration ||
                                        approvalAckPhase === "acked")
                                        ? APPROVAL_AUTO_REDIRECT_MS
                                        : null
                                }
                                onContinue={
                                    isLiveApprovalCelebration
                                        ? requestContinue
                                        : undefined
                                }
                            />
                            {isLiveApprovalCelebration &&
                            approvalAckPhase === "error" ? (
                                <Alert
                                    variant="destructive"
                                    className="mt-4 px-5 py-4"
                                >
                                    <AlertTitle>
                                        Couldn&apos;t open your console
                                    </AlertTitle>
                                    <AlertDescription>
                                        Your onboarding is approved, but we
                                        couldn&apos;t finish opening the
                                        console.
                                        <Button
                                            variant="raised"
                                            interaction="lifted"
                                            size="sm"
                                            className="mt-3"
                                            onClick={() =>
                                                void retryApprovalAck()
                                            }
                                        >
                                            Retry
                                        </Button>
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </>
                    ) : (
                        <span aria-hidden />
                    )
                ) : undefined
            }
        />
    );

    return (
        <OnboardingShell
            key={scenario.name}
            scenario={scenario}
            celebrating={isApprovalCelebration}
            hub={
                bundle ? (
                    <OnboardingApplicationProvider
                        application={bundle.application}
                        steps={bundle.steps}
                        agreements={bundle.agreements}
                        applyStepUpdate={applyStepUpdate}
                        refetch={refetch}
                    >
                        <OnboardingSubmitProvider value={submitValue}>
                            {panel}
                        </OnboardingSubmitProvider>
                    </OnboardingApplicationProvider>
                ) : (
                    panel
                )
            }
        />
    );
}
